import { saveAs } from 'file-saver';
import type { Message } from '../services/api';

// position: fixed elements repeat on every printed page in Chrome —
// this is how we get a persistent letterhead without CSS Paged Media.
const LETTERHEAD_STYLES = `
  .letterhead {
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 46px;
    background: #ffffff;
    border-bottom: 3px solid #3B82F6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    z-index: 9999;
  }
  .letterhead-left {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }
  .letterhead-sapaad {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #3A2220;
  }
  .letterhead-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #3B82F6;
    display: inline-block;
    margin-bottom: 2px;
  }
  .letterhead-product {
    font-size: 12px;
    font-weight: 500;
    color: #3B82F6;
    letter-spacing: 0.04em;
  }
  .letterhead-title {
    font-size: 11px;
    color: #6B4540;
    max-width: 50%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .print-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: 26px;
    background: #ffffff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    font-size: 10px;
    color: #666;
  }
  .print-footer-brand { color: #555; }
  .print-footer-date  { color: #666; }
`;

const CONTENT_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html { -webkit-print-color-adjust: economy; print-color-adjust: economy; }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #ffffff;
    color: #1a1a1a;
    /* top: clears fixed letterhead (46px) + gap; bottom: clears fixed footer (26px) + gap */
    padding: 62px 28px 38px;
    font-size: 13px;
    line-height: 1.6;
  }

  /* ---------- Conversation header (first page only) ---------- */
  .export-subheader {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding-bottom: 12px;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 22px;
  }
  .export-conv-title { font-size: 15px; font-weight: 700; color: #1a1a1a; }
  .export-meta       { font-size: 11px; color: #666; }

  /* ---------- Message layout ---------- */
  .message-pair { margin-bottom: 22px; }

  .user-message {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 9px;
    break-after: avoid;
    page-break-after: avoid;
  }
  .user-bubble {
    background: #306643;
    color: #fff;
    border-radius: 14px 14px 4px 14px;
    padding: 9px 15px;
    font-size: 13px;
    line-height: 1.5;
    max-width: 72%;
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* ---------- av2-response overrides for print (light / ink-saving) ---------- */
  .av2-response { color: #1a1a1a; font-size: 13px; line-height: 1.6; background: transparent; }
  .av2-response p,
  .av2-response li   { font-size: 13px !important; line-height: 1.6 !important; color: #1a1a1a !important; }
  .av2-response h1   { font-size: 16px !important; color: #1a1a1a; margin: 14px 0 7px; }
  .av2-response h2   { font-size: 14px !important; color: #1a1a1a; margin: 12px 0 6px; }
  .av2-response h3   { font-size: 13px !important; color: #444; margin: 10px 0 5px; }

  /* Force light backgrounds on LLM-generated dark inline styles */
  .av2-response div,
  .av2-response section,
  .av2-response article {
    background: transparent !important;
    color: #1a1a1a !important;
  }

  .av2-response table {
    border-collapse: collapse;
    width: 100%;
    margin: 10px 0;
    font-size: 12px !important;
  }
  .av2-response th {
    background: #f5f5f5 !important;
    color: #333 !important;
    font-weight: 600;
    padding: 7px 11px;
    border: 1px solid #ddd;
    text-align: left;
    font-size: 12px !important;
  }
  .av2-response td {
    padding: 6px 11px;
    border: 1px solid #ddd;
    color: #333 !important;
    background: #fff !important;
    font-size: 12px !important;
  }
  .av2-response tr:nth-child(even) td { background: #fafafa !important; }

  .av2-response canvas { max-width: 100%; height: auto; }

  .av2-response button[data-followup],
  .av2-response [data-av-clarification],
  .av2-response [data-buy-tokens],
  .av2-response [data-av-business-parameters],
  .av2-response [data-vantage-link],
  .av2-response .av2-chip { display: none !important; }

  .av2-response [data-section="go-deeper"],
  .av2-response .av2-go-deeper { display: none !important; }

  @media print {
    html, body {
      background: #ffffff !important;
      color: #000000 !important;
      -webkit-print-color-adjust: economy;
      print-color-adjust: economy;
    }
    body { padding-top: 62px; }
    canvas { break-inside: avoid; page-break-inside: avoid; }
    .messages > .message-pair:last-child { margin-bottom: 0; }
  }
`;

const PAGE_RULE = `
  @page {
    size: A4;
    margin: 12mm 14mm 12mm 14mm;
  }
`;

function buildMessagesHtml(messages: Message[]): string {
  const parts: string[] = [];
  let i = 0;

  while (i < messages.length) {
    const msg = messages[i];

    if (msg.message_type === 'user') {
      const userHtml = `<div class="user-message"><div class="user-bubble">${escapeHtml(msg.content)}</div></div>`;

      const next = messages[i + 1];
      const hasAssistant = next?.message_type === 'assistant' && next.html_content;
      const assistantHtml = hasAssistant
        ? `<div class="assistant-message"><div class="av2-response">${next.html_content}</div></div>`
        : '';

      parts.push(`<div class="message-pair">${userHtml}${assistantHtml}</div>`);
      i += hasAssistant ? 2 : 1;
    } else if (msg.message_type === 'assistant' && msg.html_content) {
      parts.push(
        `<div class="message-pair"><div class="assistant-message"><div class="av2-response">${msg.html_content}</div></div></div>`
      );
      i += 1;
    } else {
      i += 1;
    }
  }

  return parts.join('\n');
}

function extractInlineScripts(messages: Message[]): string {
  const parser = new DOMParser();
  return messages
    .filter((m) => m.message_type === 'assistant' && m.html_content)
    .flatMap((m) => {
      const doc = parser.parseFromString(m.html_content!, 'text/html');
      return Array.from(doc.querySelectorAll<HTMLScriptElement>('script:not([src])')).map(
        (s) => s.textContent || ''
      );
    })
    .join('\n');
}

function buildHtmlDocument(messages: Message[], conversationTitle: string, username: string): string {
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const messagesHtml  = buildMessagesHtml(messages);
  const inlineScripts = extractInlineScripts(messages);
  const title         = conversationTitle || 'Ask Vantage Conversation';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>
    ${PAGE_RULE}
    ${LETTERHEAD_STYLES}
    ${CONTENT_STYLES}
  </style>
  <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
</head>
<body>

  <div class="letterhead">
    <div class="letterhead-left">
      <span class="letterhead-sapaad">Sapaad</span>
      <span class="letterhead-dot"></span>
      <span class="letterhead-product">Ask Vantage</span>
    </div>
    <div class="letterhead-title">${escapeHtml(title)}</div>
  </div>

  <div class="print-footer">
    <span class="print-footer-brand">Confidential &middot; Sapaad Ask Vantage</span>
    <span class="print-footer-date">Exported by ${escapeHtml(username)} &middot; ${date}</span>
  </div>

  <div class="export-subheader">
    <span class="export-conv-title">${escapeHtml(title)}</span>
    <span class="export-meta">Exported by ${escapeHtml(username)} &middot; ${date}</span>
  </div>

  <div class="messages">
${messagesHtml}
  </div>

  <script>
    setTimeout(function() { try { ${inlineScripts} } catch(e) {} }, 300);
    setTimeout(function() { window.print(); }, 1000);
  </script>
</body>
</html>`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export async function exportConversation(
  messages: Message[],
  conversationTitle: string,
  username: string,
): Promise<void> {
  if (messages.length === 0) return;

  const html = buildHtmlDocument(messages, conversationTitle, username);

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  } else {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const filename = `ask-vantage-${(conversationTitle || 'conversation')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()}.html`;
    saveAs(blob, filename);
  }
}
